import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Mail, Phone, Users, GraduationCap, Target, Film, Briefcase, ChevronRight } from 'lucide-react';
import { getServiceBySlug, servicesData } from '../data/services';

const iconMap = {
  Users,
  GraduationCap,
  Target,
  Film,
  Briefcase
};

// Animation configs (extracted to module scope for stable references across renders)
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const fadeInUpOnView = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true }
};

const slideInLeftOnView = {
  initial: { opacity: 0, x: -20 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true }
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = `${service.title} - HoopIQ`;
    }
  }, [service, slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#061324] text-white">
        <div className="text-center">
          <h1 className="text-4xl font-black uppercase mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Service Not Found
          </h1>
          <button
            onClick={() => navigate('/')}
            className="bg-[#C9A84C] text-[#061324] px-6 py-3 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-[#d8b655] transition-colors"
            data-testid="not-found-back-button"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon];
  const otherServices = servicesData.filter(s => s.slug !== service.slug);

  return (
    <div className="bg-[#061324] min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid={`service-hero-${service.slug}`}>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'url(https://static.prod-images.emergentagent.com/jobs/a47baca2-6813-4a68-b147-4e346342c0a9/images/23fc2fc3f750b92679b28c4b11b4b35409e0c87aa063045fa86ff4ebe792715f.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#061324]/90 via-[#061324]/70 to-[#061324]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-[#C9A84C] hover:text-[#d8b655] mb-8 text-sm font-bold uppercase tracking-wide"
            data-testid="back-to-services"
          >
            <ArrowLeft size={16} />
            All Services
          </Link>

          <motion.div {...fadeInUp}>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-sm mb-6">
              {Icon && <Icon className="text-[#C9A84C]" size={32} />}
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mb-4 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              data-testid="service-title"
            >
              {service.title}
            </h1>

            <p
              className="text-lg md:text-xl text-[#C9A84C] font-bold uppercase tracking-wide mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {service.heroTagline}
            </p>

            <p className="text-base md:text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
              {service.overview}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-[#0A2240]" data-testid="service-features">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
            Key Features
          </div>
          <h2
            className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mb-12"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            What Makes It Different
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                {...fadeInUpOnView}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#061324] border border-white/10 rounded-sm p-6 card-hover-effect"
                data-testid={`feature-${index}`}
              >
                <h3
                  className="text-xl font-bold uppercase tracking-tight text-[#C9A84C] mb-3"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-[#94A3B8] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Clinic (if present) */}
      {service.featuredClinic && (
        <section className="py-20 bg-[#061324]" data-testid="featured-clinic">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
              Featured Clinic
            </div>
            <div className="bg-gradient-to-br from-[#0A2240] to-[#112b4d] border border-[#C9A84C]/30 rounded-sm p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                <div className="md:col-span-2">
                  <h2
                    className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mb-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {service.featuredClinic.name}
                  </h2>
                  <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed">
                    {service.featuredClinic.focus}
                  </p>
                </div>
                <div className="bg-[#061324] border border-white/10 rounded-sm p-6">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-2">
                    Date
                  </div>
                  <div
                    className="text-2xl font-black uppercase text-white"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {service.featuredClinic.date}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* What to Expect */}
      <section className="py-20 bg-[#061324]" data-testid="service-what-to-expect">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
                What To Expect
              </div>
              <h2
                className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                The Experience
              </h2>
              <ul className="space-y-4">
                {service.whatToExpect.map((item, index) => (
                  <motion.li
                    key={item}
                    {...slideInLeftOnView}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <Check className="text-[#C9A84C] mt-1 flex-shrink-0" size={20} />
                    <span className="text-[#94A3B8]">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="bg-[#0A2240] border border-white/10 rounded-sm p-8">
              <div className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
                Ideal For
              </div>
              <h3
                className="text-2xl font-bold uppercase tracking-tight text-white mb-4"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Who Should Train
              </h3>
              <p className="text-[#94A3B8] leading-relaxed mb-8">
                {service.idealFor}
              </p>

              <div className="space-y-3">
                <a
                  href="mailto:mpugh@puresweatbasketball.com"
                  className="flex items-center justify-center gap-2 w-full bg-[#C9A84C] text-[#061324] px-6 py-4 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-[#d8b655] transition-colors gold-glow"
                  data-testid="service-cta-primary"
                >
                  <Mail size={18} />
                  {service.cta.primary}
                </a>
                {service.cta.secondary && (
                  <a
                    href="tel:+15734330599"
                    className="flex items-center justify-center gap-2 w-full border-2 border-[#C9A84C] text-[#C9A84C] px-6 py-4 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-[#C9A84C] hover:text-[#061324] transition-all"
                    data-testid="service-cta-secondary"
                  >
                    <Phone size={18} />
                    {service.cta.secondary}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-[#0A2240]" data-testid="other-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
            Explore More
          </div>
          <h2
            className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mb-12"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Other Services
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherServices.map((other) => {
              const OtherIcon = iconMap[other.icon];
              return (
                <Link
                  key={other.slug}
                  to={`/services/${other.slug}`}
                  className="block bg-[#061324] border border-white/10 rounded-sm p-6 card-hover-effect group"
                  data-testid={`other-service-${other.slug}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-sm mb-4">
                    {OtherIcon && <OtherIcon className="text-[#C9A84C]" size={20} />}
                  </div>
                  <h3
                    className="text-base font-bold uppercase tracking-tight text-white mb-3 group-hover:text-[#C9A84C] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {other.title}
                  </h3>
                  <div className="flex items-center text-[#C9A84C] text-sm font-bold uppercase tracking-wide">
                    Learn More <ChevronRight size={14} className="ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
